import { forwardRef } from 'react';
import type { RefObject, MouseEvent } from 'react';
import styled from 'styled-components';

import {
  Icon,
  registerIcon,
  defaultThemeProp,
  Tooltip,
  useElement,
  useTheme
} from '@pega/cosmos-react-core';
import * as pencilIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/pencil.icon';
import * as trashIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/trash.icon';

registerIcon(pencilIcon, trashIcon);

export interface ImagePosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface ImageActionButtonsProps {
  position: ImagePosition | null;
  containerRef: RefObject<HTMLElement>;
  onEdit: (editButtonRef: HTMLButtonElement) => void;
  onDelete: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  editAriaLabel: string;
  deleteAriaLabel: string;
  editTooltip: string;
  deleteTooltip: string;
}

const ActionsContainer = styled.div<{ $visible: boolean; $top: number; $left: number }>(
  ({ theme, $visible, $top, $left }) => ({
    position: 'absolute',
    display: $visible ? 'flex' : 'none',
    gap: `calc(0.25 * ${theme.base.spacing})`,
    zIndex: theme.base['z-index'].popover,
    backgroundColor: theme.base.palette['primary-background'],
    borderRadius: theme.base['border-radius'],
    boxShadow: theme.base.shadow.low,
    padding: `calc(0.25 * ${theme.base.spacing})`,
    top: `${$top}rem`,
    left: `${$left}rem`,
    transform: 'translateX(-100%)'
  })
);

ActionsContainer.defaultProps = defaultThemeProp;

const ActionButton = styled.button(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: `calc(3 * ${theme.base.spacing})`,
  height: `calc(3 * ${theme.base.spacing})`,
  padding: 0,
  border: 'none',
  borderRadius: `calc(0.25 * ${theme.base['border-radius']})`,
  backgroundColor: 'transparent',
  color: theme.base.palette['foreground-color'],
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.base.palette['secondary-background']
  },
  '&:focus': {
    outline: 'none',
    boxShadow: theme.base.shadow.focus
  }
}));

ActionButton.defaultProps = defaultThemeProp;

const ImageActionButtons = forwardRef<HTMLButtonElement, ImageActionButtonsProps>(
  function ImageActionButtons(
    {
      position,
      containerRef,
      onEdit,
      onDelete,
      onMouseEnter,
      onMouseLeave,
      editAriaLabel,
      deleteAriaLabel,
      editTooltip,
      deleteTooltip
    },
    ref
  ) {
    const [editEl, setEditEl] = useElement<HTMLButtonElement>();
    const [deleteEl, setDeleteEl] = useElement<HTMLButtonElement>();
    const theme = useTheme();

    if (!position || !containerRef.current) {
      return null;
    }

    // Calculate position relative to the container
    const containerRect = containerRef.current.getBoundingClientRect();
    // Use theme spacing (parse rem value and convert to pixels for calculation)
    const spacingRem = parseFloat(theme.base.spacing) || 1;
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const padding = spacingRem * rootFontSize * 0.5; // Half of base spacing

    // Position at top-right of the image (convert to rem for output)
    const top = (position.top - containerRect.top + padding) / rootFontSize;
    const left = (position.left - containerRect.left + position.width - padding) / rootFontSize;

    const handleEditClick = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (editEl) {
        onEdit(editEl);
      }
    };

    const handleDeleteClick = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onDelete();
    };

    // Combine refs for edit button
    const setEditRef = (el: HTMLButtonElement | null) => {
      setEditEl(el);
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        ref.current = el;
      }
    };

    return (
      <ActionsContainer
        $visible={!!position}
        $top={top}
        $left={left}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <ActionButton
          ref={setEditRef}
          onClick={handleEditClick}
          aria-label={editAriaLabel}
          type='button'
        >
          <Icon name='pencil' />
        </ActionButton>
        {editEl && (
          <Tooltip target={editEl} showDelay='none' hideDelay='none'>
            {editTooltip}
          </Tooltip>
        )}
        <ActionButton
          ref={setDeleteEl}
          onClick={handleDeleteClick}
          aria-label={deleteAriaLabel}
          type='button'
        >
          <Icon name='trash' />
        </ActionButton>
        {deleteEl && (
          <Tooltip target={deleteEl} showDelay='none' hideDelay='none'>
            {deleteTooltip}
          </Tooltip>
        )}
      </ActionsContainer>
    );
  }
);

export default ImageActionButtons;
